import React from 'react';
import { useCountdown } from 'utils/hooks/useCountdown';
import { zeroPad } from 'utils/zeroPad';
import { twMerge } from 'tailwind-merge';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { Typography } from '@dapp/multi-lib/sdaoui/Typography';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';

interface StakingCountdownProps {
  id: string;
  date: number;
  inline?: boolean;
  timestamp: number;
}

export function StakingCountdown({
  id,
  inline = false,
  date,
  timestamp,
}: StakingCountdownProps) {
  const [days, hours, minutes] = useCountdown(date, timestamp);
  const isLocked = timestamp < date;
  const iconName = isLocked ? 'lock_locked' : 'lock_unlocked';
  const { t } = useTranslation('common');

  const toastId = 'tt-unlock-date_' + id;

  if (inline) {
    return (
      <div data-tooltip-id={toastId}>
        <Typography
          as='div'
          className={twMerge(
            cx('flex flex-row items-center gap-1 text-base', {
              ['text-[#54FAAA]']: isLocked,
              ['text-[#FB5656]']: !isLocked,
            })
          )}
        >
          <span>{zeroPad(Math.max(days, 0), 2)} d</span>
          <span>:</span>
          <span>{zeroPad(Math.max(hours, 0), 2)} h</span>
          <span>:</span>
          <span>{zeroPad(Math.max(minutes, 0), 2)} m</span>
          <Icon
            icon={iconName}
            height={16}
            width={16}
            color={isLocked ? '#54FAAA' : '#FB5656'}
          />
          {date !== 0 && (
            <Tooltip id={toastId} content={new Date(date).toString()} />
          )}
        </Typography>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-1' data-tooltip-id={toastId}>
      <Typography
        as='div'
        className={twMerge(
          cx('flex flex-row items-center gap-2 text-base', {
            ['text-[#54FAAA]']: isLocked,
            ['text-[#FB5656]']: !isLocked,
          })
        )}
      >
        <span>{zeroPad(Math.max(days, 0), 2)}</span>
        <span>:</span>
        <span>{zeroPad(Math.max(hours, 0), 2)}</span>
        <span>:</span>
        <span>{zeroPad(Math.max(minutes, 0), 2)}</span>
        <Icon
          icon={iconName}
          height={16}
          width={16}
          color={isLocked ? '#54FAAA' : '#FB5656'}
        />
      </Typography>

      <Typography
        as='div'
        className='flex flex-row items-center gap-2 text-[0.625rem]'
      >
        <span className='capitalize'>{t('day', { count: days })}</span>
        <span className='capitalize'>{t('hour', { count: hours })}</span>
        <span className='capitalize'>{t('minute', { count: minutes })}</span>
      </Typography>
      {date !== 0 && (
        <Tooltip id={toastId} content={new Date(date).toString()} />
      )}
    </div>
  );
}
